import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Bitcoin, Coins, Lock, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtUsd(v) {
  if (!v && v !== 0) return "—";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
}

function fmtQty(v) {
  if (!v && v !== 0) return null;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return `${v.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
}

function VelocityChip({ label, value, testId }) {
  const positive = value !== null && value !== undefined && value >= 0;
  const color =
    value === null || value === undefined
      ? "text-muted-foreground"
      : positive
      ? "text-green-400"
      : "text-red-400";
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <div className="flex items-center gap-1 text-[10px]" data-testid={testId}>
      <span className="text-muted-foreground">{label}</span>
      {value !== null && value !== undefined ? (
        <span className={`font-mono ${color} inline-flex items-center gap-0.5`}>
          <Icon className="w-2.5 h-2.5" />
          {positive ? "+" : ""}
          {value.toFixed(2)}%
        </span>
      ) : (
        <span className="font-mono text-muted-foreground">—</span>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, value, velocities, accent, testId, subtitle }) {
  return (
    <div
      className="rounded-lg border border-border/40 bg-muted/20 p-3"
      data-testid={testId}
    >
      <div className="flex items-start gap-1.5 text-[10px] sm:text-[11px] leading-tight text-muted-foreground mb-1">
        <Icon className={`w-3.5 h-3.5 shrink-0 mt-px ${accent || ""}`} />
        <span className="min-w-0 break-words">{label}</span>
      </div>
      <div className="font-mono text-base sm:text-lg font-bold">
        {fmtUsd(value)}
      </div>
      {subtitle ? (
        <div className="font-mono text-[10px] text-muted-foreground/80 mt-0.5" data-testid={`${testId}-subtitle`}>
          {subtitle}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-1.5">
        <VelocityChip label="7D" value={velocities?.wow} testId={`${testId}-wow`} />
        <VelocityChip label="30D" value={velocities?.mom} testId={`${testId}-mom`} />
        <VelocityChip label="1Y" value={velocities?.yoy} testId={`${testId}-yoy`} />
      </div>
    </div>
  );
}

export default function BullishTriadCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await fetch(`${API}/api/tvl/triad`);
        if (!res.ok || !(res.headers.get("content-type") || "").includes("json")) return;
        const json = await res.json();
        if (active && json?.success) setData(json);
      } catch {
        /* silent */
      }
    };
    load();
    // Poll so the LIVE badge is honest — BTC-priced bond TVL moves with spot.
    const id = setInterval(load, 30000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  const m = data?.metrics || {};
  const v = data?.velocities || {};
  const bondSubtitle = m.bond_btc_qty ? `${fmtQty(m.bond_btc_qty)} BTC enrolled` : null;
  const stxSubtitle = m.stx_matched_qty ? `≈ ${fmtQty(m.stx_matched_qty)} STX locked` : null;

  return (
    <Card className="glass-card border-orange-500/30 bg-orange-500/5" data-testid="bullish-triad-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Sparkles className="w-4 h-4 text-orange-400" />
          TVL Bond Tracker
          <Badge variant="outline" className="text-[10px] ml-auto">LIVE</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 sm:p-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <Tile
            icon={Bitcoin}
            label="sBTC TVL"
            value={m.sbtc_tvl_usd}
            velocities={v.sbtc_tvl_usd}
            accent="text-orange-400"
            testId="triad-sbtc"
          />
          <Tile
            icon={Coins}
            label="STX DeFi TVL"
            value={m.stx_defi_tvl_usd}
            velocities={v.stx_defi_tvl_usd}
            accent="text-cyan-400"
            testId="triad-stx-defi"
          />
          <Tile
            icon={Lock}
            label="Bitcoin Protocol Bonds"
            value={m.bond_tvl_usd}
            velocities={v.bond_tvl_usd}
            accent="text-purple-400"
            testId="triad-bonds"
            subtitle={bondSubtitle}
          />
          <Tile
            icon={Sparkles}
            label="STX 5% Protocol Bonds Matched"
            value={m.stxoshi_locked_usd}
            velocities={v.stxoshi_locked_usd}
            accent="text-orange-300"
            testId="triad-stxoshi-locked"
            subtitle={stxSubtitle}
          />
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          Velocity = Δ vs snapshot 7d / 30d / 365d ago. Anchors accumulate daily.
        </p>
      </CardContent>
    </Card>
  );
}
